import { useInfiniteQuery } from "@tanstack/react-query";

import { LockIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

import { TicketCommentComposer } from "@/features/tickets/components/TicketCommentComposer";
import { TicketCommentList } from "@/features/tickets/components/TicketCommentList";
import { TicketCommentSkeleton } from "@/features/tickets/components/TicketCommentSkeleton";
import { getTicketComments } from "@/features/tickets/services/ticket";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { AppError } from "@/shared/lib/utils/errors";

interface TicketCommentsPanelProps {
  projectId: string;
  ticketId: string;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_READ for this project. */
  canReadComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_CREATE for this project. */
  canCreateComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_UPDATE for this project. */
  canUpdateComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_DELETE or PROJECT_TICKET_COMMENTS_DELETE_OWN for this project. */
  canDeleteComments: boolean;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_LIKE for this project. */
  canLikeComments: boolean;
}

export function TicketCommentsPanel({ projectId, ticketId, canReadComments, canCreateComments, canUpdateComments, canDeleteComments, canLikeComments }: TicketCommentsPanelProps) {
  const { data, isPending, error, refetch, fetchNextPage, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId],
    queryFn: ({ pageParam }) => getTicketComments(projectId, ticketId, pageParam),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextStartingAfter,
    staleTime: QUERY_EXPIRATIONS.GET_TICKET_COMMENTS,
    enabled: canReadComments,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  if (!canReadComments) {
    return <EmptyState icon={LockIcon} title="No access" description="You don't have permission to view this ticket's comments." className="py-10" />;
  }

  const comments = data?.pages.flatMap((page) => page.comments) ?? [];

  return (
    <div className="flex flex-col gap-0 px-6 pb-6">
      <TicketCommentComposer projectId={projectId} ticketId={ticketId} canCreateComments={canCreateComments} />

      {error && <Error variant="modal" title="Failed to load comments" error={error as AppError | Error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {error && !errorModalOpen ? (
        <InlineError title="Failed to load comments" description="Something went wrong while fetching this ticket's comments." onRetry={handleRetry} className="py-10" />
      ) : isPending ? (
        <div className="flex flex-col divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <TicketCommentSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          <TicketCommentList projectId={projectId} ticketId={ticketId} comments={comments} canCreateComments={canCreateComments} canUpdateComments={canUpdateComments} canDeleteComments={canDeleteComments} canLikeComments={canLikeComments} />
          {comments.length > 0 && (
            <div className="flex items-center justify-between gap-4 pt-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{comments.length}</span> comments shown
              </p>
              {hasNextPage && (
                <Button variant="outline" size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
